<template>
    <div class="inline-editor"
        :class="{ editable: !readonly && !hasReadonlySlot, readonly }">
        <el-popover v-model="edit"
            transition="none"
            :visible-arrow="false"
            placement="bottom-end"
            ref="popover"
            popper-class="inline-editor__btn">
            <i title="确定"
                class="btn-edit btn-save"
                @click.stop="save"></i>
            <i title="取消"
                class="btn-edit btn-cancel"
                @click.stop="cancel"></i>
        </el-popover>

        <div class="editor-wrap"
            v-if="edit"
            v-click-out-side="cancel"
            v-popover:popover>

            <div class="editor-inner">

                <el-row v-if="hasDefaultSlot">
                    <slot :data="slotData"></slot>
                </el-row>
                <el-row v-else>
                    <el-col>
                        <el-select :clearable="isClearable"
                            v-model="val"
                            v-if="type==='select'"
                            :loading="loading"
                            :placeholder="placeholder"
                            @clear="$emit('clear')"
                            @visible-change="handleVisibleChange">
                            <el-option v-for="item in data"
                                :key="getProp(item, 'value')"
                                :label="getProp(item, 'label')"
                                :value="getProp(item, 'value')">
                            </el-option>
                        </el-select>
                        <el-input ref="input"
                            v-model="val"
                            :placeholder="placeholder"
                            :type="type"
                            :autosize="{ minRows: 1, maxRows: 6 }"
                            @keyup.native.enter.stop="enter"
                            @keyup.native.alt.83.stop="enter"
                            @keyup.native.esc.stop="cancel"
                            v-else>
                        </el-input>
                    </el-col>
                </el-row>

                <input style="display: none">

            </div>

        </div>

        <div class="normal-wrap"
            v-if="!edit"
            @click="toggle(true)">

            <div class="normal-inner"
                v-if="hasReadonlySlot">
                <slot></slot>
            </div>

            <div class="normal-inner"
                :title="getTips(rawValue)"
                :class="{ 'single-row': !this.textWrap, placeholder: !rawValue }"
                v-else>
                <slot name="label"
                    :data="rawValue">
                    {{rawValue || emptyText}}
                </slot>
            </div>

        </div>
    </div>
</template>

<script>
import clickOutSide from 'vue-click-outside'
import { types, focus, propValidator, propsValidator } from './utils'

export default {
    name: 'InlineEditor',
    directives: {
        clickOutSide
    },
    props: {
        type: {
            type: String,
            default: 'text',
            validator: value => types.indexOf(value) > -1
        },
        data: {
            type: Array,
            default() {
                return []
            }
        },
        loading: {
            type: Boolean,
            default: false
        },
        options: {
            type: Object,
            default() {
                return {
                    label: 'name',
                    value: 'id'
                }
            }
        },
        emptyText: {
            type: String,
            default: '无'
        },
        placeholder: {
            type: String,
            default() {
                if (this.type === 'select') {
                    return '请选择'
                }
                return '请输入'
            }
        },
        isClearable: {
            type: Boolean,
            default: true
        },
        value: [String, Number, Object],
        formatter: Function,
        validator: Function,
        readonly: Boolean,
        required: [Boolean, String],
        rules: {
            type: [Array, Object],
            default() {
                return []
            }
        },
        saveOnChange: {
            type: Boolean,
            default: false
        },
        closeOnSaved: {
            type: Boolean,
            default: false
        },
        closeOnError: {
            type: Boolean,
            default: true
        },
        getRef: Function,
        textWrap: Boolean,
        meta: null
    },
    data() {
        return {
            newValue: null,
            oldValue: null,

            scopeData: {
                value: null
            },
            edit: false
        }
    },
    computed: {
        isObjectValue() {
            return typeof this.value === 'object'
        },
        val: {
            get() {
                return this.newValue
            },
            set(val) {
                if (this.isObjectValue) {
                    this.newValue = val
                } else {
                    const value = val && val.trim ? val.trim() : val;
                    this.newValue = value;
                    this.scopeData.value = value
                }
            }
        },
        slotData: {
            get() {
                return this.isObjectValue ? this.newValue : this.scopeData
            },
            set(val) {
                if (!this.isObjectValue) {
                    this.scopeData.value = val
                }
            }
        },
        rawValue() {
            if (this.formatter) {
                return this.formatter(this.newValue || this.value, this.meta)
            }
            return this.newValue || this.value
        },
        hasDefaultSlot() {
            return !!this.$scopedSlots.default
        },
        hasReadonlySlot() {
            return !!this.$slots.default
        }
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                this.fill(val)
            }
        },
        scopeData: {
            deep: true,
            handler(scopeData) {
                this.newValue = scopeData.value
            }
        }
    },
    created() {
        this.$nextTick(() => {
            this.fill()
        })
    },
    methods: {
        copy(val) {
            return this.isObjectValue ? JSON.parse(JSON.stringify(val)) : val;
        },
        fill(val) {
            const value = !val && val !== 0 ? this.value : val;
            this.newValue = this.copy(value);
            if (this.hasDefaultSlot) {
                this.slotData = value
            }
        },
        getTips(val) {
            return `${this.readonly ? '' : '点击编辑\n'}${this.isObjectValue ? '' : val}`
        },
        getProp(item, key) {
            if (typeof item === 'string') {
                return item
            }
            return this.options[key] ? item[this.options[key]] : item[key]
        },
        toggle(status) {
            // hasReadonlySlot为true时，默认纯显示，不添执行编辑
            if (!this.readonly && !this.hasReadonlySlot) {
                this.edit = status;
                if (status) {
                    // cache use to rollback
                    this.oldValue = this.copy(this.newValue);
                    this.$nextTick(() => {
                        focus(this.getRef ? this.getRef() : this.$refs.input)
                    });
                }
                this.$emit(status ? 'open' : 'close')
            }
        },
        validate(newValue, oldValue, meta, callback) {
            if (this.validator) {
                return this.validator(newValue, oldValue, meta, callback)
            }
            if (this.isObjectValue) {
                const props = typeof this.rules === 'object' ? Object.keys(this.rules) : Object.keys(this.value);
                return propsValidator(newValue, meta, this.required, this.rules, props, callback)
            }
            return propValidator(newValue, meta, this.required, this.rules, callback)
        },
        save() {
            this.validate(this.newValue, this.value, this.meta, error => {
                if (error) {
                    this.$message.error(error);
                    if (this.closeOnError) {
                        this.rollback();
                        this.toggle(false)
                    }
                } else {
                    this.change()
                }
            })
        },
        change() {
            this.$emit('input', this.newValue);
            if (!this.saveOnChange || JSON.stringify(this.value) !== JSON.stringify(this.newValue)) {
                this.$emit('change', { newValue: this.newValue, oldValue: this.value }, this.meta, this.afterChange)
            }
            if (!this.closeOnSaved) {
                this.toggle(false)
            }
        },
        /**
         * change之后触发
         * @param savedStatus [true:保存成功|false:保存失败]
         */
        afterChange(savedStatus) {
            if (this.closeOnSaved) {
                if (!savedStatus) {
                    this.rollback()
                }
                this.toggle(false)
            }
        },
        cancel() {
            this.rollback();
            this.toggle(false)
        },
        enter(e) {
            if (this.type === 'text' || e.ctrlKey || e.altKey) {
                this.save()
            }
        },
        rollback() {
            if (this.oldValue !== null) {
                this.val = this.oldValue;
                this.$emit('input', this.oldValue);
            }
        },
        handleVisibleChange(visible) {
            if (visible) {
                this.$emit('visibleChange')
            }
        }
    }
}
</script>

<style lang="less">
.inline-editor {
    width: 100%;
    min-height: 36px;
    position: relative;
    font-size: 14px;
    &.editable,
    &.readonly {
        height: 36px;
    }
    .normal-wrap {
        position: relative;
        cursor: text;
        padding: 2px 0;
        display: inline-block;
        max-width: 100%;
        .normal-inner {
            line-height: 30px;
            min-height: 32px;
            padding: 0 10px;
            border: 1px solid transparent;
            border-radius: 3px;
            position: relative;
            &.single-row {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                color: #333;
            }
            &.placeholder {
                font-style: italic;
                color: #666;
            }
            &:after {
                content: '';
                background: #f0f0f0 url("../../common/img/edit.png") no-repeat center;
                width: 30px;
                height: 100%;
                border-left: 1px solid #ccc;
                border-radius: 0 3px 3px 0;
                position: absolute;
                right: 0;
                top: 0;
                display: block;
                cursor: pointer;
                visibility: hidden;
            }
        }
    }
    &.editable {
        .normal-wrap {
            &:hover {
                .normal-inner {
                    border: 1px solid #ccc;
                    padding: 0 40px 0 10px;
                    &:after {
                        visibility: visible;
                    }
                }
            }
        }
    }
    .editor-wrap {
        position: absolute;
        min-height: 100%;
        width: 100%;
        z-index: 1;
        .editor-inner {
            .el-textarea {
                padding: 2px 0;
            }
            .el-select {
                width: 100%;
            }
            input {
                border-radius: 3px;
                border: 1px solid #A3CBFF;
                line-height: 32px;
                font-size: 14px;
                &:hover {
                    border: 1px solid #4c99fe;
                }
            }
            textarea {
                border-radius: 3px;
                border: 1px solid #A3CBFF;
                padding: 0 10px;
                line-height: 30px;
                min-height: 32px;
                font-size: 14px;
                &:hover {
                    border: 1px solid #4c99fe;
                }
            }
        }
    }
    &__btn.el-popover {
        min-width: 70px;
        padding: 3px;
        margin: 0;
        border-radius: 3px;
        border: 1px solid #C6DFFF;
        box-shadow: 0 2px 6px 0 rgba(77, 161, 255, .2);
        text-align: center;
        font-size: 0;
        .btn-edit {
            width: 24px;
            height: 24px;
            display: inline-block;
            cursor: pointer;
            & + .btn-edit {
                margin-left: 6px;
            }
        }
        .btn-cancel {
            background: url("../../common/img/cancel.png") no-repeat center;
        }
        .btn-save {
            background: url("../../common/img/yes.png") no-repeat center;
        }
    }
}
</style>
